import { useState } from 'react'
import { Form, Input, Button, Card, Typography, Alert } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'

const { Text } = Typography

// Animated Logo Component
const AnimatedLogo = () => (
    <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        marginBottom: 24,
    }}>
        {/* Telegram Icon with gradient */}
        <div
            style={{
                width: 100,
                height: 100,
                background: 'linear-gradient(135deg, rgb(0, 136, 204) 0%, rgb(0, 180, 216) 100%)',
                borderRadius: 28,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 12px 48px rgba(0, 136, 204, 0.4)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'default',
                animation: 'iconPulse 3s ease-in-out infinite',
            }}
            className="login-icon"
        >
            <svg
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="white"
                style={{ transform: 'translateX(-2px)' }}
            >
                <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" />
            </svg>
        </div>

        {/* Rotating 3D Text */}
        <div style={{
            perspective: '800px',
            height: 50,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            <div
                style={{
                    fontSize: 36,
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.5px',
                    whiteSpace: 'nowrap',
                    animation: 'rotateText 10s linear infinite',
                    transformStyle: 'preserve-3d',
                }}
                className="rotating-text"
            >
                TG <span style={{ color: '#00C2FF' }}>Manager</span>
            </div>
        </div>

        {/* Subtitle */}
        <div style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            marginTop: -8,
        }}>
            Telegram CRM
        </div>
    </div>
)

export default function Login() {
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const { login, error, clearError } = useAuthStore()

    const onFinish = async (values: { username: string; password: string }) => {
        setLoading(true)
        clearError()
        try {
            await login(values.username, values.password)
            navigate('/')
        } catch {
            // Error is handled by store
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {/* CSS Animations */}
            <style>{`
                @keyframes rotateText {
                    0% { transform: rotateY(0deg); }
                    100% { transform: rotateY(360deg); }
                }
                
                @keyframes iconPulse {
                    0%, 100% { 
                        transform: scale(1); 
                        box-shadow: 0 12px 48px rgba(0, 136, 204, 0.4);
                    }
                    50% { 
                        transform: scale(1.02); 
                        box-shadow: 0 16px 60px rgba(0, 136, 204, 0.55);
                    }
                }
                
                @keyframes bgShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                
                .login-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bg-base);
                    padding: 20px;
                    overflow: hidden;
                    position: relative;
                }
                
                .login-page::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 30% 30%, rgba(0, 136, 204, 0.08) 0%, transparent 40%),
                                radial-gradient(circle at 70% 70%, rgba(0, 180, 216, 0.05) 0%, transparent 40%);
                    animation: bgShift 15s ease-in-out infinite;
                    pointer-events: none;
                }
                
                .login-card {
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    max-width: 420px;
                    background: var(--bg-surface) !important;
                    backdrop-filter: blur(20px);
                    border: 1px solid var(--border) !important;
                    border-radius: 24px !important;
                    box-shadow: var(--shadow-xl) !important;
                }
                
                .login-input {
                    background: var(--bg-subtle) !important;
                    border-color: var(--border) !important;
                    border-radius: 12px !important;
                    height: 52px !important;
                    transition: all 0.3s ease !important;
                }
                
                .login-input:hover,
                .login-input:focus {
                    border-color: var(--telegram) !important;
                    box-shadow: 0 0 0 2px rgba(0, 136, 204, 0.1) !important;
                }
                
                .login-input input {
                    background: transparent !important;
                    color: var(--text-primary) !important;
                }
                
                .login-input .ant-input-prefix {
                    color: var(--text-muted) !important;
                }
                
                .login-btn {
                    height: 52px !important;
                    border-radius: 12px !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    background: var(--brand-primary) !important;
                    border: none !important;
                    box-shadow: 0 8px 32px rgba(0, 136, 204, 0.2) !important;
                    transition: all 0.3s ease !important;
                }
                
                .login-btn:hover {
                    background: var(--brand-primary-hover) !important;
                    transform: translateY(-2px) !important;
                    box-shadow: 0 12px 40px rgba(0, 136, 204, 0.3) !important;
                }
                
                .login-btn:active {
                    transform: translateY(0) !important;
                }
            `}</style>

            <div className="login-page">
                <Card
                    className="login-card"
                    styles={{ body: { padding: 48 } }}
                >
                    <AnimatedLogo />

                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{
                                marginBottom: 24,
                                background: 'rgba(255, 77, 79, 0.1)',
                                border: '1px solid rgba(255, 77, 79, 0.3)',
                                borderRadius: 12,
                            }}
                            closable
                            onClose={clearError}
                        />
                    )}

                    <Form
                        name="login"
                        onFinish={onFinish}
                        autoComplete="off"
                        layout="vertical"
                        size="large"
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Введите логин' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Логин"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Введите пароль' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="Пароль"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                className="login-btn"
                            >
                                Войти
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{ marginTop: 32, textAlign: 'center' }}>
                        <Text style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            Telegram Multi-Account Manager v1.0
                        </Text>
                    </div>
                </Card>
            </div>
        </>
    )
}
